import type { GameDefinition, Outcome, Seat, Viewer } from '@campfire/engine';
import { ACE_HIGH, deal, shuffle, standardDeck, type Card, type StandardRank } from '@campfire/cards';

export interface GoFishOptions {
  /** Cards per hand; null = 7 for 2–3 players, 5 for 4+. */
  readonly handSize: number | null;
  /** Your turn, you hold cards, but every other hand is empty: draw from the ocean, or pass. */
  readonly noOneToAsk: NoOneToAsk;
}

export type NoOneToAsk = 'draw' | 'pass';

export interface AskEvent {
  readonly asker: Seat;
  readonly target: Seat;
  readonly rank: StandardRank;
  /** Cards handed over by the target (public: they are shown when given). */
  readonly given: number;
  /** Set when the asker had to go fishing. */
  readonly fished: boolean;
  /** Private to the asker unless it matched the asked rank. */
  readonly drawn: Card | null;
  readonly luckyDraw: boolean;
  readonly booked: readonly StandardRank[];
}

export interface GoFishState {
  readonly hands: readonly (readonly Card[])[];
  readonly ocean: readonly Card[];
  readonly books: readonly (readonly StandardRank[])[];
  readonly turn: Seat;
  readonly last: AskEvent | null;
  readonly result: Outcome | null;
  /** House rule from options; absent means 'draw'. */
  readonly noOneToAsk?: NoOneToAsk;
}

export type GoFishAction =
  | { readonly type: 'ask'; readonly target: Seat; readonly rank: StandardRank }
  | { readonly type: 'refill' }
  | { readonly type: 'fish' }
  | { readonly type: 'pass' };

export interface GoFishView {
  readonly you: Seat | null;
  readonly hand: readonly Card[] | null;
  readonly handCounts: readonly number[];
  readonly ocean: number;
  readonly books: readonly (readonly StandardRank[])[];
  readonly turn: Seat;
  readonly last: (Omit<AskEvent, 'drawn'> & { readonly drawn: Card | null }) | null;
  readonly result: Outcome | null;
}

const RANK_COUNT = 13;

const sortHand = (cards: readonly Card[]) =>
  [...cards].sort((a, b) => ACE_HIGH.compare(a, b) || (a.suit ?? '').localeCompare(b.suit ?? ''));

/** Lays down any complete four-of-a-kind from a hand. */
function bookOut(hand: readonly Card[]): { hand: Card[]; booked: StandardRank[] } {
  const counts = new Map<string, number>();
  for (const c of hand) counts.set(c.rank, (counts.get(c.rank) ?? 0) + 1);
  const booked = [...counts].filter(([, n]) => n === 4).map(([r]) => r as StandardRank);
  return { hand: hand.filter((c) => !booked.includes(c.rank as StandardRank)), booked };
}

/** The active player holds cards but nobody else does, so there is no one to ask. */
const noOneToAsk = (s: GoFishState) =>
  (s.hands[s.turn]?.length ?? 0) > 0 && s.hands.every((h, i) => i === s.turn || h.length === 0);

const replace = <T>(arr: readonly T[], i: number, v: T): T[] => arr.map((x, j) => (j === i ? v : x));
const next = (s: GoFishState, from: Seat) => (from + 1) % s.hands.length;

function finish(s: GoFishState): GoFishState {
  const total = s.books.reduce((n, b) => n + b.length, 0);
  if (total < RANK_COUNT) return s;
  const counts = s.books.map((b) => b.length);
  const max = Math.max(...counts);
  const winners = counts.flatMap((c, i) => (c === max ? [i] : []));
  const scores = Object.fromEntries(counts.map((c, i) => [i, c]));
  return { ...s, result: { winners, draw: false, scores, reason: winners.length > 1 ? 'tied-books' : 'most-books' } };
}

export const goFish: GameDefinition<GoFishState, GoFishAction, GoFishView, GoFishOptions> = {
  id: 'go-fish',
  name: 'Go Fish',
  rules: [
    'Collect books: all four cards of a rank. The player with the most books when all 13 have been made wins; ties share the win.',
    'Hands are 7 cards for 2 or 3 players and 5 for 4 or more. Any four of a kind you are dealt is laid down as a book straight away.',
    'On your turn pick another player and a rank you hold, and ask them for it. You must hold at least one card of the rank you ask for.',
    'If they have any, they hand over all of them and you go again. If not, they say Go Fish and you draw one card from the ocean.',
    'If the drawn card is the rank you asked for, everyone sees it and you go again. Otherwise your turn ends.',
    'Books are laid down automatically. If you run out of cards you draw one from the ocean and carry on; with the ocean empty, you are skipped.',
    'Setting: Starting hand. Standard, or 5, 6 or 7 cards each.',
    'Setting: When nobody else has cards. You hold cards but every other player is out: either draw one from the ocean, then pass, or pass without drawing.',
    'The app enforces everything. Players with no cards cannot be asked.',
  ],
  minSeats: 2,
  maxSeats: 6,
  defaultOptions: { handSize: null, noOneToAsk: 'draw' },
  optionSpecs: [
    {
      key: 'handSize',
      label: 'Starting hand',
      choices: [
        { value: null, label: 'Standard (7 cards for 2–3 players, 5 for 4+)' },
        ...[5, 6, 7].map((n) => ({ value: n, label: `${n} cards` })),
      ],
    },
    {
      key: 'noOneToAsk',
      label: 'When nobody else has cards',
      help: 'It is your turn and you hold cards, but every other player is out.',
      choices: [
        { value: 'draw', label: 'Draw one from the ocean, then pass' },
        { value: 'pass', label: 'Pass without drawing' },
      ],
    },
  ],

  setup({ seats, options, rng }) {
    const size = options.handSize ?? (seats <= 3 ? 7 : 5);
    if (!Number.isInteger(size) || size < 1 || size * seats > 52) throw new RangeError('handSize out of range');
    const { hands, remaining } = deal(shuffle(standardDeck(), rng), seats, { perHand: size });
    const booked = hands.map(bookOut);
    return finish({
      hands: booked.map((b) => sortHand(b.hand)),
      ocean: remaining,
      books: booked.map((b) => b.booked),
      turn: 0,
      last: null,
      result: null,
      noOneToAsk: options.noOneToAsk,
    });
  },

  activeSeats: (s) => (s.result || (s.hands[s.turn]?.length ?? 0) === 0 || noOneToAsk(s) ? [] : [s.turn]),

  validate(s, a, seat) {
    if (a.type !== 'ask') return { ok: false, reason: 'only the engine refills or passes' };
    if (!Number.isInteger(a.target) || a.target < 0 || a.target >= s.hands.length) return { ok: false, reason: 'no such player' };
    if (a.target === seat) return { ok: false, reason: 'cannot ask yourself' };
    if ((s.hands[a.target]?.length ?? 0) === 0) return { ok: false, reason: 'that player has no cards' };
    if (!s.hands[seat]?.some((c) => c.rank === a.rank)) return { ok: false, reason: 'you must hold a card of the rank you ask for' };
    return { ok: true };
  },

  apply(s, a) {
    const me = s.turn;
    if (a.type === 'pass') return { ...s, turn: next(s, me) };
    if (a.type === 'refill' || a.type === 'fish') {
      const [card, ...ocean] = s.ocean;
      const b = bookOut([...(s.hands[me] ?? []), card as Card]);
      return finish({
        ...s,
        ocean,
        // Refill keeps the turn (you now have a card to ask with); fishing ends it.
        turn: a.type === 'fish' ? next(s, me) : me,
        hands: replace(s.hands, me, sortHand(b.hand)),
        books: replace(s.books, me, [...(s.books[me] ?? []), ...b.booked]),
      });
    }

    const target = s.hands[a.target] as readonly Card[];
    const taken = target.filter((c) => c.rank === a.rank);
    let mine = [...(s.hands[me] as readonly Card[])];
    let hands = s.hands;
    let ocean = s.ocean;
    let drawn: Card | null = null;
    let turn = me;

    if (taken.length > 0) {
      mine = [...mine, ...taken];
      hands = replace(hands, a.target, target.filter((c) => c.rank !== a.rank));
    } else if (ocean.length > 0) {
      drawn = ocean[0] as Card;
      ocean = ocean.slice(1);
      mine = [...mine, drawn];
      if (drawn.rank !== a.rank) turn = next(s, me);
    } else {
      turn = next(s, me);
    }

    const b = bookOut(mine);
    hands = replace(hands, me, sortHand(b.hand));
    const last: AskEvent = {
      asker: me,
      target: a.target,
      rank: a.rank,
      given: taken.length,
      fished: taken.length === 0,
      drawn,
      luckyDraw: drawn !== null && drawn.rank === a.rank,
      booked: b.booked,
    };
    return finish({ ...s, hands, ocean, turn, last, books: replace(s.books, me, [...(s.books[me] ?? []), ...b.booked]) });
  },

  /**
   * Empty-handed active player draws from the ocean, or is skipped when it is empty.
   * With no one to ask, the `noOneToAsk` house rule applies (FR-GOFISH-09).
   */
  autoAction(s) {
    if (s.result) return null;
    if ((s.hands[s.turn]?.length ?? 0) === 0) return s.ocean.length > 0 ? { type: 'refill' } : { type: 'pass' };
    if (!noOneToAsk(s)) return null;
    return (s.noOneToAsk ?? 'draw') === 'draw' && s.ocean.length > 0 ? { type: 'fish' } : { type: 'pass' };
  },

  view(s, viewer: Viewer) {
    const you = viewer.kind === 'seat' ? viewer.seat : null;
    const last = s.last;
    const showDrawn = last !== null && (last.luckyDraw || you === last.asker);
    return {
      you,
      hand: you === null ? null : (s.hands[you] ?? null),
      handCounts: s.hands.map((h) => h.length),
      ocean: s.ocean.length,
      books: s.books,
      turn: s.turn,
      last: last === null ? null : { ...last, drawn: showDrawn ? last.drawn : null },
      result: s.result,
    };
  },

  outcome: (s) => s.result,

  enumerateActions(s, seat) {
    if (s.turn !== seat || s.result) return [];
    const ranks = [...new Set((s.hands[seat] ?? []).map((c) => c.rank as StandardRank))];
    const out: GoFishAction[] = [];
    s.hands.forEach((h, target) => {
      if (target !== seat && h.length > 0) for (const rank of ranks) out.push({ type: 'ask', target, rank });
    });
    return out;
  },
};
